import { type Result, ok, err } from 'neverthrow';
import { getEntries } from '@morph-mapper/utils';
import { serializeFn } from './functions';
import { ParserError } from './error';
import { serializeZod } from './zod';
import type {
  SerializedSchema,
  SerializedSchemaConfig,
  SerializedSchemaConfigOption,
  SerializedSchemaConfigSection,
  SerializedSchemaEntry,
  SerializedSchemaMap,
  SerializedSchemaNode,
  RuntimeSchema,
  RuntimeSchemaConfig,
  RuntimeSchemaConfigOption,
  RuntimeSchemaConfigSection,
  RuntimeSchemaEntry,
  RuntimeSchemaMap,
  RuntimeSchemaNode,
} from './types';

const isSchemaEntry = (node: RuntimeSchemaNode): node is RuntimeSchemaEntry => {
  return 'validation' in node;
};

const omitNullish = <T extends Record<string, unknown>>(value: T): T => {
  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => entryValue != null),
  ) as T;
};

const serializeSchemaEntry = (
  entry: RuntimeSchemaEntry,
): Result<SerializedSchemaEntry, ParserError> => {
  const { validation, prefill, ...rest } = entry;

  const serialized = serializeZod(validation);
  if (serialized.isErr()) return err(serialized.error);

  return ok(
    omitNullish({
      ...rest,
      validation: serialized.value,
      prefill: prefill ? serializeFn(prefill) : undefined,
    }),
  );
};

const serializeSchemaNode = (
  node: RuntimeSchemaNode,
): Result<SerializedSchemaNode, ParserError> => {
  if (isSchemaEntry(node)) {
    return serializeSchemaEntry(node);
  }

  return serializeSchemaMap(node);
};

const serializeSchemaMap = (
  schemaMap: RuntimeSchemaMap,
): Result<SerializedSchemaMap, ParserError> => {
  const { values, ...rest } = schemaMap;

  const serializedValues: Record<string, SerializedSchemaNode> = {};
  for (const [key, value] of getEntries(values)) {
    const serialized = serializeSchemaNode(value);
    if (serialized.isErr()) return err(serialized.error);

    serializedValues[key] = serialized.value;
  }

  return ok(
    omitNullish({
      ...rest,
      values: serializedValues,
    }),
  );
};

const serializeConfigOption = (
  option: RuntimeSchemaConfigOption,
): Result<SerializedSchemaConfigOption, ParserError> => {
  const { type, ...rest } = option;

  const serializedType = serializeZod(type);
  if (serializedType.isErr()) return err(serializedType.error);

  return ok({
    ...rest,
    type: serializedType.value,
  });
};

const serializeConfigSection = (
  section: RuntimeSchemaConfigSection,
): Result<SerializedSchemaConfigSection, ParserError> => {
  const { options, mutate, ...rest } = section;

  const serializedOptions: Record<string, SerializedSchemaConfigOption> = {};
  for (const [key, option] of getEntries(options)) {
    const serialized = serializeConfigOption(option);
    if (serialized.isErr()) return err(serialized.error);

    serializedOptions[key] = serialized.value;
  }

  return ok({
    ...rest,
    options: serializedOptions,
    mutate: serializeFn(mutate),
  });
};

const serializeConfig = (
  config: RuntimeSchemaConfig | undefined,
): Result<SerializedSchemaConfig, ParserError> => {
  if (!config) return ok({});

  const serializedSections: Record<string, SerializedSchemaConfigSection> = {};
  for (const [key, section] of getEntries(config)) {
    const serialized = serializeConfigSection(section);
    if (serialized.isErr()) return err(serialized.error);

    serializedSections[key] = serialized.value;
  }

  return ok(serializedSections);
};

export const serializeSchema = (
  schema: RuntimeSchema,
): Result<SerializedSchema, ParserError> => {
  const serializedConfig = serializeConfig(schema.config);
  if (serializedConfig.isErr()) return err(serializedConfig.error);

  const serializedDefinition = serializeSchemaMap(schema.definition);
  if (serializedDefinition.isErr()) return err(serializedDefinition.error);

  return ok({
    updateTemplateDiscardFields: [...schema.updateTemplateDiscardFields],
    config: serializedConfig.value,
    definition: serializedDefinition.value,
  });
};
